/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, DialogActions } from 'omakase';
import { LocalDate, LocalTime } from '@js-joda/core';
import { OfferingPartyItem, isEventVariety, offeringPartyItem } from '@tock/shared/utils/offerings';
import { RouteComponentProps } from 'react-router';
import { ServiceOptions } from '@tock/shared/constants/ServiceConstants';
import { StoreType } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { WAITLIST_PAGES } from 'routes/business/WaitlistRoute';
import { computeDisplayedTimes, isOfferingEventType } from 'utils/offerings';
import { consumer, core } from '@tock/coretypes';
import { isEmail } from '@tock/shared/utils/FormValidators';
import { pluralize } from '@tock/shared/utils/pluralize';
import { reduxForm } from 'redux-form';
import { twentyFourHourFormatter } from '@tock/i18n';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ExperienceNotFound from 'components/business/search/ExperienceNotFound';
import Form from '@tock/shared/components/forms/Form';
import WaitlistDialogContactPage from 'components/business/waitlist-dialog/WaitlistDialogContactPage';
import WaitlistDialogPreferences from 'components/business/waitlist-dialog/WaitlistDialogPreferences';
import _ from 'lodash';
import queryString from '@tock/query-string';

export interface ExperienceOption {
  label: string;
  sizes: number[];
  services: SelectOption[];
  value: number | null;
  partyItem: OfferingPartyItem;
}

export interface SeatingAreaOption {
  name: string;
  id: number | undefined;
}

export type Page = keyof typeof WAITLIST_PAGES;

interface OwnProps {
  business: Partial<consumer.IBusinessConfig>;
  date?: string;
  openDates: string[];
  detectedLocation: core.DetectedLocation;
  displayedTimes: string[];
  experienceId?: number;
  isAuthenticated: boolean;
  isWidget: boolean;
  location: RouteComponentProps['location'];
  offerings: consumer.ConsumerOfferings;
  onClose(): void;
  onSubmit(formData: any): void;
  patron?: core.Patron;
  showError(message: string): void;
  size?: number;
  ticketTypeCalendarDetails: consumer.TicketTypeCalendarDetails[];
  ticketTypes: core.TicketType[];
  isFeatureEnabled(featureName: core.Feature.FeatureName): boolean;
  page: Page;
  setPage: (page: Page) => void;
}

export interface Fields {
  email: string;
  experience: number;
  firstName: string;
  isoCountryCode: string;
  lastName: string;
  partySize: number;
  phone: string;
  phoneCountryCode: string;
  selectedDate: string[];
  desiredEndTime: string;
  desiredStartTime: string;
  guestNote: string;
}

export type WaitlistDialogProps = OwnProps & ReduxFormProps<Fields>;

const validate = (values: Fields, page: Page) => {
  const errors: ReduxFormErrors<Fields> = {};

  if (page === 'PREFERENCES_PAGE') {
    if (!values.selectedDate || values.selectedDate.length === 0) {
      errors.selectedDate = ['Please select at least one date'];
    }
  }

  if (page === 'CONTACT_PAGE') {
    if (!values.firstName) {
      errors.firstName = 'Please enter a first name.';
    }
    if (!values.lastName) {
      errors.lastName = 'Please enter a last name.';
    }
    if (!values.email || !isEmail(values.email)) {
      errors.email = 'Please enter a valid email';
    }
  }

  return errors;
};

const anyServiceOption: SelectOption = {
  key: 'any',
  label: 'Any time',
  value: 'any',
};

const allServiceOptions = _.concat(_.clone(anyServiceOption), ServiceOptions);

class WaitlistDialog extends BaseComponent<WaitlistDialogProps> {
  getAnyExperienceOption(sizes: number[]): ExperienceOption {
    const validServiceTypes: string[] = _.uniq(_.flatMap(this.props.ticketTypes, 'serviceType'));

    const serviceOptions = _.filter(allServiceOptions, (option) => {
      return option.value === 'any' || _.includes(validServiceTypes, option.key);
    });

    return {
      label: 'Any experience',
      sizes,
      partyItem: 'guest',
      services: serviceOptions,
      value: null,
    };
  }

  getExperienceOptions(): ExperienceOption[] {
    const { ticketTypes } = this.props;

    const experienceOptions = _.map(
      ticketTypes,
      ({ name, serviceType, variety, descriptiveVariety, purchaseSize, id }) => {
        const filteredServiceOptions = _.filter(ServiceOptions, (option) => {
          return _.includes(serviceType, option.key);
        });

        return {
          label: name,
          partyItem: offeringPartyItem(variety, descriptiveVariety),
          sizes: _.sortBy(purchaseSize),
          services:
            filteredServiceOptions && filteredServiceOptions.length === 1
              ? // for 'Event' ticketTypes, we always show 'Any time' as the selectable
                // option, even though the underlying serviceType that's written to the
                // DB is the 'DINNER' type. Otherwise, we show the single actually available
                // service type.
                isEventVariety(variety)
                ? [anyServiceOption]
                : filteredServiceOptions
              : [anyServiceOption, ...filteredServiceOptions],
          value: id,
        };
      }
    );

    // We use _.sortBy() rather than Array.prototype.sort() to get an actual
    // numeric sort here. Otherwise 100 will sort before 2, surprisingly.
    const allPurchaseSizes = _.sortBy(_.union(..._.map(ticketTypes, 'purchaseSize')));

    const anyExperienceOption = this.getAnyExperienceOption(allPurchaseSizes);
    return [anyExperienceOption].concat(experienceOptions);
  }

  getPurchaseSizeOptions(selectedExperience: ExperienceOption): SelectOption[] {
    return _.map(selectedExperience.sizes, (size: number) => ({
      key: `${size}`,
      label: pluralize(size, selectedExperience.partyItem),
      value: size,
    }));
  }

  getDisplayedTimesOptions(): SelectOption[] {
    const displayedTimes = computeDisplayedTimes(
      this.props.business,
      this.props.offerings,
      LocalDate.now().toString()
    );
    return displayedTimes;
  }

  getSelectedExperience(experienceOptions: ExperienceOption[]): ExperienceOption {
    return _.find(experienceOptions, {
      value: this.props.fields.experience.value,
    }) as ExperienceOption;
  }

  getSelectedDesiredStartTime(displayedTimes: string[]): string {
    return (
      displayedTimes.find((time) => time === this.props.fields.desiredStartTime.value) ||
      displayedTimes[displayedTimes.length - 1]
    );
  }

  getSelectedDesiredEndTime(displayedTimes: string[]): string {
    return (
      displayedTimes.find((time) => time === this.props.fields.desiredEndTime.value) ||
      displayedTimes[displayedTimes.length - 1]
    );
  }

  getSelectedPartySize(
    selectedExperience: ExperienceOption,
    partySizeOptions: SelectOption[]
  ): SelectOption {
    let selectedPartySize = _.find(
      partySizeOptions,
      (entry) => entry.value === this.props.fields.partySize.value
    );

    if (!selectedPartySize) {
      selectedPartySize = partySizeOptions[0];
      this.props.fields.partySize.onChange(selectedExperience.sizes[0]);
    }

    return selectedPartySize;
  }

  render() {
    const {
      business,
      displayedTimes,
      fields: {
        desiredEndTime,
        desiredStartTime,
        email,
        experience,
        firstName,
        isoCountryCode,
        lastName,
        partySize,
        phone,
        phoneCountryCode,
        selectedDate,
        guestNote,
      },
      handleSubmit,
      invalid,
      isAuthenticated,
      isWidget,
      location,
      offerings,
      onClose,
      showError,
      submitting,
      ticketTypeCalendarDetails,
      page,
      setPage,
    } = this.props;

    const experienceOptions = this.getExperienceOptions();
    const selectedExperience = this.getSelectedExperience(experienceOptions);
    const selectedDesiredStartTime = this.getSelectedDesiredStartTime(displayedTimes);
    const selectedDesiredEndTime = this.getSelectedDesiredEndTime(displayedTimes);

    if (_.isUndefined(selectedExperience)) {
      return (
        <ExperienceNotFound
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          businessName={business.name!}
          isAuthenticated={isAuthenticated}
          isOfferingEvent={isOfferingEventType(selectedExperience)}
          isPrivateExperience={false}
          isWidget={isWidget}
          location={location}
          onButtonClick={onClose}
        />
      );
    }

    const purchaseSizeOptions = this.getPurchaseSizeOptions(selectedExperience);
    const selectedPartySize = this.getSelectedPartySize(selectedExperience, purchaseSizeOptions);
    const sharedProps = {
      displayedTimes,
      selectedDate,
      selectedDesiredStartTime,
      selectedDesiredEndTime,
      selectedExperience,
      selectedPartySize,
      submitting,
    };

    const renderContent = () => {
      switch (page) {
        case WAITLIST_PAGES.PREFERENCES_PAGE:
          return (
            <WaitlistDialogPreferences
              {...sharedProps}
              business={business}
              desiredEndTime={desiredEndTime}
              desiredStartTime={desiredStartTime}
              displayedTimes={displayedTimes}
              experience={experience}
              experienceOptions={experienceOptions}
              guestNote={guestNote}
              offerings={offerings}
              partySize={partySize}
              partySizeOptions={purchaseSizeOptions}
              selectedDates={selectedDate}
              showError={showError}
              ticketTypeCalendarDetails={ticketTypeCalendarDetails}
            />
          );
        case WAITLIST_PAGES.CONTACT_PAGE:
          return (
            <WaitlistDialogContactPage
              {...sharedProps}
              email={email}
              firstName={firstName}
              isoCountryCode={isoCountryCode}
              lastName={lastName}
              phone={phone}
              phoneCountryCode={phoneCountryCode}
            />
          );
        default:
          return null;
      }
    };

    const renderActions = () => {
      switch (page) {
        case WAITLIST_PAGES.PREFERENCES_PAGE:
          if (!isAuthenticated) {
            return (
              <Button
                disabled={invalid || submitting}
                fullWidth
                variant="primary"
                size="medium"
                onClick={() => {
                  setPage(WAITLIST_PAGES.CONTACT_PAGE);
                  // touch an optional form control to trigger validation on page change
                  // this keeps the submit button disabled until valid values are present
                  // no errors are shown to the user until they interact with a the field
                  this.props.touch('isoCountryCode');
                }}
              >
                Continue
              </Button>
            );
          } else {
            return (
              <Button
                type="submit"
                disabled={invalid || submitting}
                fullWidth
                variant="primary"
                size="medium"
                onClick={() => {
                  handleSubmit();
                }}
              >
                Set Notify
              </Button>
            );
          }
        case WAITLIST_PAGES.CONTACT_PAGE:
          return (
            <>
              <Button
                disabled={submitting}
                fullWidth
                variant="basic"
                size="medium"
                onClick={() => setPage(WAITLIST_PAGES.PREFERENCES_PAGE)}
              >
                Back
              </Button>

              <Button
                type="submit"
                disabled={invalid || submitting}
                fullWidth
                variant="primary"
                size="medium"
                onClick={async () => {
                  handleSubmit();
                }}
              >
                Set Notify
              </Button>
            </>
          );
        default:
          return null;
      }
    };

    return (
      <>
        <Form
          onSubmit={handleSubmit}
          css={(theme: Theme) => css`
            padding: 0 ${theme.spacing(3.5)}px;
            ${theme.breakpoints.down('xs')} {
              padding: 0;
            }
          `}
        >
          {renderContent()}
        </Form>

        <DialogActions>{renderActions()}</DialogActions>
      </>
    );
  }
}

export default reduxForm<OwnProps, Fields>(
  {
    form: 'WaitlistDialog',
    fields: [
      'email',
      'experience',
      'firstName',
      'isoCountryCode',
      'lastName',
      'partySize',
      'phone',
      'phoneCountryCode',
      'selectedDate',
      'guestNote',
      'desiredStartTime',
      'desiredEndTime',
    ],
    validate: (values: Fields, props: OwnProps) => {
      return validate(values, props.page);
    },
  },
  (state: StoreType, props: OwnProps) => {
    const { detectedLocation } = state.app;
    const { date, displayedTimes, patron } = props;
    const getSeatingAreaOptions = () => {
      const { offerings } = props;

      const experience = _.find(
        offerings.experience,
        (experience) => experience.id === props.experienceId
      );
      const seatingAreas = experience?.seatingArea || [];

      const seatingAreaOptions = seatingAreas.map(
        (seatingArea) =>
          ({
            name: seatingArea.name + ' seating',
            id: seatingArea.id,
          }) as SeatingAreaOption
      );

      return seatingAreaOptions;
    };

    const getSelectedSeatingAreaName = (seatingAreaOptions: SeatingAreaOption[]) => {
      const seatingArea = _.find(seatingAreaOptions, {
        id: Number(queryString.parse(location.search).seatingAreaId),
      }) as SeatingAreaOption;

      return seatingArea?.name || '';
    };

    const seatingAreas = getSeatingAreaOptions();

    const isSeatingAreaWaitlistFeatureFlagEnabled = props.isFeatureEnabled(
      'PREPOPULATE_WAITLIST_WITH_SEATING_AREA'
    );

    const searchParams = queryString.parse(props.location.search);

    const getDefaultTimesFromSearchParams = () => {
      const hasSelectedATimeSlot = searchParams.time;
      if (hasSelectedATimeSlot) {
        const localTimeDesiredTime = LocalTime.parse(searchParams.time);
        const startTime = localTimeDesiredTime.minusHours(1);
        const endTime = localTimeDesiredTime.plusHours(1);

        let desiredEndTime = twentyFourHourFormatter.format(endTime);
        let desiredStartTime = twentyFourHourFormatter.format(startTime);

        const isOutsideBusinessHours = (time: string) => !displayedTimes.includes(time);
        if (isOutsideBusinessHours(desiredStartTime)) {
          desiredStartTime = displayedTimes[0];
        }

        if (isOutsideBusinessHours(desiredEndTime)) {
          desiredEndTime = displayedTimes[displayedTimes.length - 1];
        }
        return {
          desiredEndTime,
          desiredStartTime,
        };
      } else {
        return {
          desiredStartTime: displayedTimes[0],
          desiredEndTime: displayedTimes[displayedTimes.length - 1],
        };
      }
    };

    let waitlistTimeFramesFields;

    try {
      waitlistTimeFramesFields = getDefaultTimesFromSearchParams();
    } catch (_error) {
      waitlistTimeFramesFields = {
        desiredStartTime: displayedTimes[0],
        desiredEndTime: displayedTimes[displayedTimes.length - 1],
      };
    }
    const phoneCountryCode =
      patron?.phoneCountryCode || detectedLocation?.phoneCountryCode || '+1 US';
    const isoCountryCode = patron?.isoCountryCode || detectedLocation?.isoCountryCode || 'US';

    return {
      initialValues: {
        email: props.patron?.email,
        // If experiences is undefined, default to null
        // in order to default to the Any option.
        experience: !_.isUndefined(props.experienceId) ? props.experienceId : null,
        firstName: props.patron?.firstName,
        isoCountryCode,
        lastName: props.patron?.lastName,
        partySize: props.size,
        phone: props.patron?.phone,
        phoneCountryCode,
        selectedDate: date ? [date] : [],
        guestNote: !isSeatingAreaWaitlistFeatureFlagEnabled
          ? ''
          : getSelectedSeatingAreaName(seatingAreas),
        ...waitlistTimeFramesFields,
      },
    };
  }
)(WaitlistDialog);
